import {
  type Component,
  type JSX,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import type { SearchProviderId } from "../searchProviders";
import SlashMenu from "./SlashMenu";
import {
  type SlashItemId,
  detectSlashTrigger,
  initialSlashMenuState,
  reduceSlashMenu,
} from "./slashMenuModel";

interface InputShellProps {
  value: string;
  onInput: (next: string) => void;
  onSubmit: () => void;
  onSelectSlashItem: (id: SlashItemId) => void;
  /** Arms a search provider — `/search` is a two-stage choice, not a toggle. */
  onSelectProvider: (id: SearchProviderId) => void;
  /** Currently armed provider, so the submenu can mark it. */
  armedProvider: SearchProviderId | null;
  // Async-by-default: the input stays interactive during generation.
  // The brief forbids a "generating…" placeholder — the streaming
  // cursor inside the response is the only indicator needed.
  placeholder?: string;
  // Controls rendered on the left, right after the `+` button (e.g. the voice
  // mic) — grouped with `+` rather than across the textarea.
  leading?: JSX.Element;
  trailing?: JSX.Element;
  // Exposes an imperative focus() to the parent so it can keep the
  // composer focused on load / thread-entry. Mirrors the `triggerRef`
  // callback pattern used by AttachmentPicker / AttachmentsLibrary.
  focusRef?: (focus: () => void) => void;
}

// The neuro input primitive — a `+` button, a real textarea styled as
// a single-line cursor surface, and the slash menu. Used in every state
// (blank, streaming, active thread, error). Per handoff §3 the wordmark
// + ⌘K hint + input are pixel-identical across all states; this
// component owns the "input" half of that invariant.
const InputShell: Component<InputShellProps> = (props) => {
  const [menu, setMenu] = createSignal(initialSlashMenuState());
  let shellEl: HTMLDivElement | undefined;
  let textareaEl: HTMLTextAreaElement | undefined;

  const closeMenu = () => setMenu((s) => reduceSlashMenu(s, { kind: "close" }));
  const openMenu = () => setMenu((s) => reduceSlashMenu(s, { kind: "open" }));
  const toggleMenu = () =>
    setMenu((s) => reduceSlashMenu(s, { kind: "toggle" }));

  const handleSelect = (id: SlashItemId) => {
    setMenu((s) => reduceSlashMenu(s, { kind: "selectItem", itemId: id }));
    // If the user opened the menu via `/`-typing, clear the slash
    // command so they can type their actual prompt next.
    if (props.value.startsWith("/")) {
      props.onInput("");
    }
    props.onSelectSlashItem(id);
    // Search stays open on the provider stage — keep focus in the menu so
    // Escape steps back rather than landing in the textarea.
    if (id !== "search") {
      textareaEl?.focus();
    }
  };

  const handleSelectProvider = (id: SearchProviderId) => {
    setMenu((s) => reduceSlashMenu(s, { kind: "selectProvider", providerId: id }));
    props.onSelectProvider(id);
    textareaEl?.focus();
  };

  const handleBack = () =>
    setMenu((s) => reduceSlashMenu(s, { kind: "escape" }));

  // Auto-grow the textarea height to fit content so the user can see
  // their full prompt as they type. Without this, `rows={1}` clips long
  // content and the textarea internally scrolls — pushing the start of
  // the prompt out of view as the user keeps typing. Capped via
  // `max-height` in styles.css; overflow-y: auto handles the rare case
  // a prompt exceeds the cap.
  const resizeTextarea = () => {
    if (!textareaEl) return;
    // Where `field-sizing: content` is supported (modern browsers), the
    // textarea sizes to its content natively and correctly — setting an
    // explicit height here would override it AND reintroduce the scrollHeight
    // "phantom trailing line" at the wrap boundary. Only run the JS fallback
    // when field-sizing is unavailable.
    if (
      typeof CSS !== "undefined" &&
      CSS.supports?.("field-sizing", "content")
    ) {
      return;
    }
    textareaEl.style.height = "auto";
    textareaEl.style.height = `${textareaEl.scrollHeight}px`;
  };

  // Resize on every value change — covers programmatic clears (after
  // submit) as well as parent-driven updates, not just user typing.
  createEffect(() => {
    // Track value reactively so the effect re-runs on each change.
    void props.value;
    queueMicrotask(resizeTextarea);
  });

  const handleInput = (next: string) => {
    props.onInput(next);
    const caret = textareaEl?.selectionStart ?? next.length;
    if (detectSlashTrigger(next, caret)) {
      openMenu();
    } else if (menu().open && !next.startsWith("/")) {
      // The user backspaced past the leading slash — close the menu.
      closeMenu();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Escape is owned solely by the document listener below. A keydown here
    // bubbles to it, so handling Escape in both places would run the reducer
    // twice on one keypress (providers -> items -> closed).
    if (e.key === "Escape") return;
    // Enter sends, Shift+Enter newlines. The textarea grows naturally
    // when text wraps; no per-state height variant.
    if (e.key === "Enter" && !e.shiftKey && !menu().open) {
      e.preventDefault();
      if (props.value.trim().length > 0) props.onSubmit();
    }
  };

  // Outside-click closes the menu. Bound on mount, cleaned on unmount.
  onMount(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menu().open) return;
      if (!shellEl) return;
      // `composedPath()` is captured at dispatch, so it still contains the
      // clicked node even when the handler runs *after* a re-render has
      // unmounted it. `e.target`/`contains()` cannot: selecting Search swaps
      // the item list for the provider stage, detaching the very button that
      // was clicked, so containment read false and this closed the menu the
      // click had just opened — making the provider stage unreachable.
      const path = e.composedPath?.() ?? [];
      if (path.includes(shellEl)) return;
      // Fall back to containment for environments without composedPath.
      if (path.length === 0 && e.target && shellEl.contains(e.target as Node)) {
        return;
      }
      setMenu((s) => reduceSlashMenu(s, { kind: "outsideClick" }));
    };
    document.addEventListener("click", onDocClick);

    // Escape, for the whole shell. The textarea's own keydown cannot be the
    // owner: selecting Search unmounts the clicked button and focus falls to
    // <body>, so the submenu's step-back was unreachable from real input.
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !menu().open) return;
      e.preventDefault();
      setMenu((s) => reduceSlashMenu(s, { kind: "escape" }));
    };
    document.addEventListener("keydown", onDocKeyDown);

    onCleanup(() => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onDocKeyDown);
    });

    props.focusRef?.(() => textareaEl?.focus());
  });

  return (
    <div class="neuro-input-shell" ref={shellEl}>
      <div class="neuro-input-line">
        <button
          type="button"
          class="neuro-input-plus"
          aria-label="Add"
          aria-expanded={menu().open}
          onClick={(e) => {
            e.stopPropagation();
            toggleMenu();
            // Return focus to the textarea so the next `?`/`/` keystroke
            // is typed into the composer instead of triggering a global
            // shortcut. The menu stays open (focus doesn't close it).
            // Skipped on touch — there's no single-key hijack without a
            // hardware keyboard, and force-focus would pop the on-screen
            // keyboard over the just-opened menu.
            if (!window.matchMedia?.("(pointer: coarse)")?.matches) {
              textareaEl?.focus();
            }
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        {props.leading}
        <textarea
          ref={textareaEl}
          class="neuro-input-textarea"
          rows={1}
          placeholder={props.placeholder ?? "Ask anything"}
          value={props.value}
          onInput={(e) => handleInput(e.currentTarget.value)}
          onKeyDown={handleKeyDown}
        />
        {props.trailing}
      </div>
      <SlashMenu
        open={menu().open}
        stage={menu().stage}
        armed={props.armedProvider}
        onSelect={handleSelect}
        onSelectProvider={handleSelectProvider}
        onBack={handleBack}
      />
    </div>
  );
};

export default InputShell;
